import * as React from 'react';
import { memo, useMemo } from 'react';

import { Pressable } from '@rbilabs/universal-components';
import { View } from 'react-native';
import { StyleSheet } from 'react-native';

import Picture from 'components/picture';
import SanityBlockRenderer, { createFixedTypographyConfig } from 'components/sanity-block-renderer';
import { Box, Typography } from 'components/ui';
import { LoyaltyQuestStatus } from 'generated/graphql-gateway';
import { primitive, spacing } from 'styles/constants';

import { IQuest } from '../loyalty-quest/types';
import { canActivateQuest } from '../loyalty-quest/utils';
import { CrownPatternBackground } from '../quest-drawer-content/crown-pattern-background';
import { QuestProgressBar } from '../quest-progress-bar';

import { QuestBadge } from './quest-badge';
import { QuestEndDateMessage } from './quest-end-date-message';
import { QuestCallToAction, QuestCardContainer, StyledLink } from './styled';
import { IQuestCardProps } from './types';

const IMAGE_HEADER_HEIGHT = 85;

export const QuestCard = memo(
  ({
    image,
    altText,
    title,
    callToAction,
    callToActionText,
    onPressCard,
    onShowTermsPress,
    linkText,
    description,
    howToRedeem,
    isQuestCompleted,
    isActivating,
    steps,
    status,
    endDate,
  }: IQuestCardProps) => {
    // Standardize Sanity content typography with design system tokens
    const contentStylesOverrides = createFixedTypographyConfig({
      fontSize: primitive.fontSizes.sm,
      lineHeight: 'lg',
    });

    const shouldDisplayProgressBarByStatus = [
      LoyaltyQuestStatus.ACTIVATED,
      LoyaltyQuestStatus.NOT_COMPLETED,
      LoyaltyQuestStatus.COMPLETED,
    ].includes(status);

    const CardContent = useMemo(() => {
      return (
        <Box style={styles.cardContent}>
          <Typography variant="headerTwo" style={styles.questCardTitle}>
            {title}
          </Typography>
          <QuestEndDateMessage endDate={endDate} />

          {!!steps && shouldDisplayProgressBarByStatus && (
            <View style={styles.progressBarContainer}>
              <QuestProgressBar steps={steps} />
            </View>
          )}

          {!isQuestCompleted && howToRedeem ? (
            <SanityBlockRenderer
              marginTop="$1_5"
              marginBottom="$4"
              content={howToRedeem}
              {...contentStylesOverrides}
            />
          ) : (
            <Typography mb="$4">{description}</Typography>
          )}

          <Box
            style={styles.actionRow}
            // row reverse to display the link aligned to the right
          >
            {!!callToActionText && (
              <QuestCallToAction
                _text={{ variant: 'copyTwo', fontSize: 'sm' }}
                onPress={callToAction}
                isLoading={isActivating}
                disabled={isActivating}
              >
                {callToActionText}
              </QuestCallToAction>
            )}
            {!!linkText && (
              <StyledLink
                onPress={e => {
                  e?.stopPropagation();
                  onShowTermsPress?.();
                }}
              >
                {linkText}
              </StyledLink>
            )}
          </Box>
        </Box>
      );
    }, [
      callToAction,
      callToActionText,
      contentStylesOverrides,
      description,
      endDate,
      howToRedeem,
      isActivating,
      isQuestCompleted,
      linkText,
      onShowTermsPress,
      shouldDisplayProgressBarByStatus,
      steps,
      title,
    ]);

    return (
      <QuestCardContainer>
        <Pressable
          onPress={onPressCard}
          // When the card has no press action (e.g. purchase quests) avoid the
          // misleading pointer cursor NativeBase Pressable shows on web by default.
          _web={{ cursor: onPressCard ? 'pointer' : 'default' }}
        >
          <View style={styles.questCardImageContainer}>
            <CrownPatternBackground style={StyleSheet.absoluteFillObject} />
            {!!image && (
              <Picture
                maxHeight={IMAGE_HEADER_HEIGHT}
                image={image}
                flexDirection="column"
                alt={altText || 'quest image'}
              />
            )}
            {!canActivateQuest(status) && (
              <View style={styles.questCardHeader}>
                <QuestBadge quest={{ steps, status } as IQuest} />
              </View>
            )}
          </View>

          {CardContent}
        </Pressable>
      </QuestCardContainer>
    );
  }
);

const styles = StyleSheet.create({
  cardContent: {
    padding: spacing.$4,
  },
  actionRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  questCardImageContainer: {
    position: 'relative',
    // Pin the header to a fixed height so the crown pattern always shows
    // (when no `image` is provided) and so the image's intrinsic aspect
    // ratio can't grow the container on native. `overflow: 'hidden'` clips
    // any contained image rendering that would otherwise bleed past it.
    height: IMAGE_HEADER_HEIGHT,
    overflow: 'hidden',
    borderTopLeftRadius: primitive.$borderRadius,
    borderTopRightRadius: primitive.$borderRadius,
  },
  questCardHeader: {
    flexDirection: 'row-reverse',
    width: '100%',
    justifyContent: 'space-between',
    position: 'absolute',
    top: 10,
    right: 10,
  },
  questCardTitle: {
    marginBottom: spacing.$1_5,
  },
  progressBarContainer: {
    marginTop: spacing.$1_5,
    marginBottom: spacing.$1_5,
  },
});
